import { toast } from "@gigpro/ui/Toast";
import { ApiError } from "@sidegigllc/api";
import {
  customFetch,
  type AdminMatchDashboardGigsCandidatesRetrieveParams,
  AdminMatchDashboardGigsGrantedProsRetrieveOrdering,
  type AdminMatchDashboardGigsListParams,
  getAdminMatchDashboardGigsCandidatesRetrieveInfiniteQueryKey,
  getAdminMatchDashboardGigsCandidatesRetrieveInfiniteQueryOptions,
  getAdminMatchDashboardGigsFilterOptionsRetrieveQueryOptions,
  getAdminMatchDashboardGigsGrantedProsRetrieveInfiniteQueryKey,
  getAdminMatchDashboardGigsGrantedProsRetrieveInfiniteQueryOptions,
  getAdminMatchDashboardGigsLastCallPreviewRetrieveQueryKey,
  getAdminMatchDashboardGigsRemindPreviewRetrieveQueryKey,
  getAdminMatchDashboardGigsListQueryKey,
  getAdminMatchDashboardGigsListQueryOptions,
  getAdminMatchDashboardGigsRetrieveQueryKey,
  getAdminMatchDashboardGigsRetrieveQueryOptions,
  getAdminMatchDashboardGigsSummaryRetrieveQueryKey,
  getAdminMatchDashboardGigsSummaryRetrieveQueryOptions,
  getAdminMatchDashboardGigsWaitlistedProsRetrieveInfiniteQueryKey,
  getAdminMatchDashboardGigsWaitlistedProsRetrieveInfiniteQueryOptions,
  type MatchDashboardCandidate as GeneratedMatchDashboardCandidate,
  type MatchDashboardCandidatePro as GeneratedMatchDashboardCandidatePro,
  type MatchDashboardGigDetail as GeneratedMatchDashboardGigDetail,
  type MatchDashboardGigList as GeneratedMatchDashboardGigList,
  type PatchedStafferGigUpdateRequest,
  stafferGigApplicationsBonusPartialUpdate,
  stafferGigApplicationsBulkBonusCreate,
  useAdminMatchDashboardGigsLastCallPreviewRetrieve,
  useAdminMatchDashboardGigsRemindPreviewRetrieve,
  useStafferDirectOffersCreate,
  useStafferGigApplicationsBulkDismissCreate,
  useStafferGigApplicationsBulkRemindCreate,
  useStafferGigApplicationsDestroy,
  useStafferGigApplicationsRemindCreate,
  useStafferGigsGrantedProsBonusCreate,
  useStafferGigsGrantedProsDismissCreate,
  useStafferGigsGrantedProsRemindCreate,
  useStafferGigsPartialUpdate,
  type GigOutreachSurfaceEnum,
  type MatchDashboardPossibleMatchesProIdsResponse,
} from "@sidegigllc/api/client";
import {
  type InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { DirectOfferNote } from "domains/admin/components/DirectOfferNoteDialog";
import type { OvertimeSelection } from "domains/admin/components/OvertimeRuleDialog";

export type {
  LastCallCohortPreview,
  MatchDashboardCandidatePro,
  MatchDashboardGrantedPro,
  MatchDashboardSummary,
  MatchDashboardWaitlistedPro,
  MatchDashboardWaitlistedProStatusEnum,
  RemindPreview,
} from "@sidegigllc/api/client";

export type MatchDashboardFilterOptionsItem = { id: number; name: string };

export type MatchDashboardFilterOptions = {
  markets: ReadonlyArray<MatchDashboardFilterOptionsItem>;
  skills: ReadonlyArray<MatchDashboardFilterOptionsItem>;
  locations: ReadonlyArray<MatchDashboardFilterOptionsItem>;
  types: ReadonlyArray<string>;
  selection_sources: ReadonlyArray<string>;
  application_types: ReadonlyArray<string>;
  candidate_statuses: ReadonlyArray<string>;
};

export type MatchDashboardUrgency = {
  urgency_label: "CONSERVATIVE" | "MODERATE" | "AGGRESSIVE";
  offers_per_slot: number;
  min_score: number;
};

export type MatchDashboardLocationCoords = { lat: number | null; lng: number | null };

/** Lead-time hold bands, mirroring `WAVE_DELAY_BANDS` in sidegig-api (api/match/services.py). */
export type MatchDelayRule = "lead_gt_72h" | "lead_48_72h" | "lead_24_48h" | "lead_16_24h";

export type MatchDashboardMatchDelay = {
  is_delayed: boolean;
  reason: string | null;
  // Optional: the web deploys ahead of the API, so these can be absent at runtime.
  delay_rule?: MatchDelayRule | null;
  hold_window_minutes?: number | null;
  delayed_until?: string | null;
  posting_offers_sent?: number;
  bypass_threshold?: number;
};

export type MatchDashboardTimelineEvent = {
  timestamp: Date | null;
  event: string;
  description: string;
  actor?: string | null;
  app_id: number | null;
  selection_source: string | null;
  application_type: string | null;
  /** The admin send an offer belongs to, or that a DIRECT_OFFERS_SENT event is (CORE-8711). */
  outreach_id?: string | null;
  note?: string;
  reason?: string | null;
};

export type MatchDashboardRecommendedWage = {
  recommended_wage_amount: number;
  recommended_wage_amount_boosted: number;
};

export type MatchDashboardWageContext = {
  market_min_wage: number | null;
  market_avg_wage: number | null;
  location_avg_wage: number | null;
};

export type MatchDashboardFillContext = {
  organic: number;
  last_call: number;
  waitlist: number;
  v3_confirmed: number;
  v3_recommended: number;
  business_do: number;
  admin_at_posting: number;
  admin_blast: number;
  conversion_offer: number;
  legacy_do: number;
  v3_do: number;
};

export type MatchDashboardPushSummaryKind = {
  message_type: string;
  sent: number;
  failed: number;
  skipped: number;
  skipped_by_reason: Record<string, number>;
  // null when some of this kind's rows were pruned, so a distinct count is no longer possible
  pros_reached: number | null;
};

// Pros who opened the shift in the app; null when the backend could not read the counts
export type MatchDashboardGigEngagement = {
  viewers: number;
  views: number;
  viewed_after_push: number;
  organic: number;
  confirmed_viewers: number;
};

export type MatchDashboardScoreDistribution = {
  scored_count: number;
  avg: number | null;
  max: number | null;
  min: number | null;
  std_dev: number | null;
  high_count: number;
  viable_count: number;
};

export type MatchQualityStats = {
  pairings_total: number;
  avg_rank: number | null;
  min_rank: number | null;
  max_rank: number | null;
  rank_gte_85: number;
  rank_gte_65: number;
  rank_gte_40: number;
  dispatch_eligible_count: number;
};

/** Hand-maintained, so it lags the API; see `MatchEligibilityBanner` for why that is safe. */
export type MatchEligibilityReasonCode = "LATE_OFFER_POOL_EXHAUSTED" | "LATE_OFFERS_HANDED_TO_LAST_CALL";

export type MatchEligibilityBlockingReason = {
  code: MatchEligibilityReasonCode;
  severity: "informational" | "warning" | "error";
  summary: string;
  detail: string;
  /** `engineering` means ops cannot act on it: a misconfigured gate, not a supply problem. */
  remediation_audience: "staffer" | "pro" | "business" | "engineering";
  fields: Record<string, unknown>;
};

export type MatchEligibility = {
  is_eligible: boolean;
  blocking_reasons: ReadonlyArray<MatchEligibilityBlockingReason>;
};

export type MatchDashboardApplicationBreakdownItem = {
  selection_source: string | null;
  type: string | null;
  total: number;
  selected: number;
  pending: number;
  confirmed: number;
  cancelled: number;
  confirm_pct: number;
};

export type MatchDashboardFactorBreakdown = {
  factors: Array<{
    factor: string;
    raw: unknown;
    feature: number | null;
    weight: number | null;
    impact: number | null;
    note: string;
  }>;
  intercept: number;
  total_impact: number;
  log_odds: number;
  score: number;
};

export type NeedsAttentionReason =
  | "NO_VIABLE_PAIRINGS"
  | "POOL_EXHAUSTED"
  | "LAST_CALL_TAKEOVER"
  | "POOL_CHANGED"
  | "MANUAL";

export type MatchDashboardGigList = Omit<GeneratedMatchDashboardGigList, "urgency"> & {
  urgency: MatchDashboardUrgency;
  needs_attention?: boolean;
  needs_attention_reason?: NeedsAttentionReason | null;
  is_first_5_for_location?: boolean;
  last_match_wave_at?: string | null;
  match_wave_count?: number;
};

export type MatchDashboardRequiredCertification = { id: number; name: string };
export type MatchDashboardCertificationRequirement = {
  id: number;
  name: string;
  requirement_level: "REQUIRED" | "PREFERRED";
};
export type MatchDashboardCrossShiftSkill = { skill_name: string; coefficient: number };

export type MatchDashboardGigDetail = Omit<
  GeneratedMatchDashboardGigDetail,
  | "urgency"
  | "match_delay"
  | "location_coords"
  | "recommended_wage"
  | "wage_context"
  | "fill_context"
  | "push_summary"
  | "engagement"
  | "score_distribution"
  | "timeline"
  | "application_breakdown"
  | "top_unapplied_pros"
  | "waves"
> & {
  urgency: MatchDashboardUrgency;
  match_delay: MatchDashboardMatchDelay;
  location_coords: MatchDashboardLocationCoords | null;
  recommended_wage: MatchDashboardRecommendedWage | null;
  wage_context: MatchDashboardWageContext | null;
  fill_context: MatchDashboardFillContext;
  // optional until every backend serving the admin build returns it
  push_summary?: ReadonlyArray<MatchDashboardPushSummaryKind>;
  engagement?: MatchDashboardGigEngagement | null;
  score_distribution: MatchDashboardScoreDistribution | null;
  match_quality_stats: MatchQualityStats | null;
  match_eligibility: MatchEligibility;
  timeline: ReadonlyArray<MatchDashboardTimelineEvent>;
  application_breakdown: ReadonlyArray<MatchDashboardApplicationBreakdownItem>;
  top_unapplied_pros: ReadonlyArray<Record<string, unknown>>;
  waves: ReadonlyArray<Record<string, unknown>>;
  // force-cast through unknown — not guaranteed populated at runtime, guard with ?? []
  required_certifications?: ReadonlyArray<MatchDashboardRequiredCertification>;
  certification_requirements?: ReadonlyArray<MatchDashboardCertificationRequirement>;
  cross_shift_skills?: ReadonlyArray<MatchDashboardCrossShiftSkill>;
  location_to_market_center_miles?: number | null;
};

export type MatchDashboardCandidateStatus = "confirmed" | "offer_sent" | "pending" | "declined" | "dismissed";

export type MatchDashboardCandidate = Omit<
  GeneratedMatchDashboardCandidate,
  "status" | "factor_breakdown" | "match_score_variables" | "pro"
> & {
  status: MatchDashboardCandidateStatus;
  factor_breakdown: MatchDashboardFactorBreakdown | null;
  match_score_variables: Record<string, unknown> | null;
  pro: GeneratedMatchDashboardCandidatePro & { completion_percentage: number | null };
};

export type MatchDashboardCandidatesPage = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Array<MatchDashboardCandidate>;
};

export type MatchDashboardGigListPage = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Array<MatchDashboardGigList>;
};

export type MatchDashboardGigListStatus = "all" | "at_risk" | "unsolved";

export type MatchDashboardGigListFillStatus = "unfilled" | "filled" | "at_risk" | "has_at_risk";

export type MatchDashboardGigListFilters = Omit<
  AdminMatchDashboardGigsListParams,
  "fill_status" | "market_id" | "skill_id"
> & {
  status?: MatchDashboardGigListStatus;
  fill_status?: MatchDashboardGigListFillStatus;
  market_id?: Array<number>;
  skill_id?: Array<number>;
  needs_attention?: boolean;
  is_first_5_for_location?: boolean;
};

export type MatchDashboardApplicantFilters = AdminMatchDashboardGigsCandidatesRetrieveParams & {
  page?: number;
  page_size?: number;
};

export const matchDashboardGigListQueryOptions = (params: MatchDashboardGigListFilters) => {
  const { status, ...rest } = params;
  const wireParams = {
    ...rest,
    status_filter: status,
  } as unknown as AdminMatchDashboardGigsListParams;
  return getAdminMatchDashboardGigsListQueryOptions(wireParams, {
    query: {
      select: (data) =>
        ({
          count: data.count,
          next: data.next ?? null,
          previous: data.previous ?? null,
          results: data.results,
        }) as unknown as MatchDashboardGigListPage,
    },
  });
};

export const matchDashboardGigDetailQueryOptions = (gigId: number | null) =>
  getAdminMatchDashboardGigsRetrieveQueryOptions<MatchDashboardGigDetail>(gigId ?? 0, {
    query: { enabled: gigId !== null && gigId > 0, select: (data) => data as unknown as MatchDashboardGigDetail },
  });

export const matchDashboardCandidatesInfiniteQueryOptions = (
  gigId: number | null,
  params: MatchDashboardApplicantFilters = {},
) =>
  getAdminMatchDashboardGigsCandidatesRetrieveInfiniteQueryOptions<
    InfiniteData<MatchDashboardCandidatesPage, AdminMatchDashboardGigsCandidatesRetrieveParams["page"]>
  >(gigId ?? 0, params, {
    query: {
      getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
      enabled: gigId !== null && gigId > 0,
    },
  });

/** Granted Pros take the candidates' filters; an ordering they do not support falls back to latest grant first. */
export const matchDashboardGrantedProsInfiniteQueryOptions = (
  gigId: number | null,
  { ordering, ...params }: MatchDashboardApplicantFilters = {},
) =>
  getAdminMatchDashboardGigsGrantedProsRetrieveInfiniteQueryOptions(
    gigId ?? 0,
    {
      ...params,
      ordering: Object.values(AdminMatchDashboardGigsGrantedProsRetrieveOrdering).find((o) => o === ordering),
    },
    {
      query: {
        getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
        enabled: gigId !== null && gigId > 0,
      },
    },
  );

export const matchDashboardWaitlistedProsInfiniteQueryOptions = (gigId: number, pageSize: number) =>
  getAdminMatchDashboardGigsWaitlistedProsRetrieveInfiniteQueryOptions(
    gigId,
    { page_size: pageSize },
    {
      query: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
        enabled: gigId > 0,
      },
    },
  );

export type FirstTimeProsMode = "INCLUDE" | "EXCLUDE" | "ONLY";

export type PossibleMatchesFilterParams = {
  market_id?: Array<number>;
  active_since?: string;
  first_time_pros?: FirstTimeProsMode;
  pro_fullname?: string;
  previous_positions?: Array<number>;
  has_certifications_for_previous_positions?: boolean;
  badge_types?: Array<string>;
  certifications?: Array<number>;
  pro_certifications?: Array<string>;
  is_verified?: boolean;
  rating_gte?: number;
  has_background_check?: boolean;
  has_worked_together?: boolean;
  worker_type?: string;
  w2_onboarded?: boolean;
  min_skillset_rating?: number;
  min_completion_percentage?: number;
  min_shifts?: number;
};

export type PossibleMatchesParams = PossibleMatchesFilterParams & {
  recommended_only?: boolean;
  ordering?: string;
  page_size?: number;
};

export type PossibleMatchPro = {
  pro_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string | null;
  admin_url: string;
  avg_rating: number | null;
  completion_percentage: number | null;
  shifts_completed: number | null;
  headshot_public_id: string | null;
  badges: ReadonlyArray<string>;
  match_score: number | null;
  combined_score: number | null;
  distance_in_miles: number | null;
  cross_coefficient: number | null;
  is_certified: boolean;
  pro_lat: number | null;
  pro_lng: number | null;
  shifts_at_location: number;
  shifts_at_location_with_skill: number;
  match_score_variables: Record<string, unknown>;
  is_at_risk: boolean;
  at_risk_reasons: ReadonlyArray<string>;
  is_verified: boolean;
  market_id: number | null;
};

export type PossibleMatchesPage = {
  count?: number;
  count_is_capped?: boolean;
  next: string | null;
  previous: string | null;
  results: ReadonlyArray<PossibleMatchPro>;
};

const possibleMatchesKey = (gigId: number) => ["match-dashboard", "possible-matches", gigId] as const;

type QueryValue = string | number | boolean;

const possibleMatchesQuery = (params: Record<string, QueryValue | Array<QueryValue> | undefined>) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) for (const item of value) query.append(key, String(item));
    else query.append(key, String(value));
  }
  return query.toString();
};

export const possibleMatchesUrl = (gigId: number, params: PossibleMatchesParams, page: number) =>
  `/api/admin/match-dashboard/gigs/${gigId}/possible-matches/?${possibleMatchesQuery({ ...params, page })}`;

/**
 * Every pro id the list shows for these filters, up to the Select all cap. Not the generated call: its URL builder
 * stringifies Dates and joins arrays, so it would not send the exact params the list sent.
 */
export const fetchPossibleMatchProIds = (gigId: number, params: PossibleMatchesParams) =>
  customFetch<MatchDashboardPossibleMatchesProIdsResponse>(
    `/api/admin/match-dashboard/gigs/${gigId}/possible-matches/pro-ids/?${possibleMatchesQuery(params)}`,
  );

export function usePossibleMatches(gigId: number | null, params: PossibleMatchesParams, enabled = true) {
  return useInfiniteQuery(
    infiniteQueryOptions({
      queryKey: [...possibleMatchesKey(gigId ?? 0), params],
      queryFn: ({ pageParam, signal }) =>
        customFetch<PossibleMatchesPage>(possibleMatchesUrl(gigId ?? 0, params, pageParam), { signal }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
      placeholderData: keepPreviousData,
      enabled: enabled && gigId !== null && gigId > 0,
    }),
  );
}

export const isPossibleMatchesRefreshing = (query: { isLoading: boolean; isPlaceholderData: boolean }) =>
  query.isLoading || query.isPlaceholderData;

export const matchDashboardSummaryQueryOptions = () => getAdminMatchDashboardGigsSummaryRetrieveQueryOptions();

export const matchDashboardFilterOptionsQueryOptions = () =>
  getAdminMatchDashboardGigsFilterOptionsRetrieveQueryOptions({
    query: { select: (data) => data as unknown as MatchDashboardFilterOptions },
  });

function useInvalidateGig(gigId: number | undefined) {
  const queryClient = useQueryClient();
  return () => {
    if (gigId === undefined) return;
    void queryClient.invalidateQueries({ queryKey: getAdminMatchDashboardGigsRetrieveQueryKey(gigId) });
    // infinite query — must invalidate via infinite key helper, not the plain one
    void queryClient.invalidateQueries({
      queryKey: getAdminMatchDashboardGigsCandidatesRetrieveInfiniteQueryKey(gigId),
    });
    void queryClient.invalidateQueries({
      queryKey: getAdminMatchDashboardGigsGrantedProsRetrieveInfiniteQueryKey(gigId),
    });
    void queryClient.invalidateQueries({ queryKey: possibleMatchesKey(gigId) });
    void queryClient.invalidateQueries({
      queryKey: getAdminMatchDashboardGigsWaitlistedProsRetrieveInfiniteQueryKey(gigId),
    });
    void queryClient.invalidateQueries({ queryKey: getAdminMatchDashboardGigsListQueryKey() });
    void queryClient.invalidateQueries({ queryKey: getAdminMatchDashboardGigsSummaryRetrieveQueryKey() });
    // The cohort toggles are what move this number, and they all save through the mutations below.
    void queryClient.invalidateQueries({ queryKey: getAdminMatchDashboardGigsLastCallPreviewRetrieveQueryKey(gigId) });
    void queryClient.invalidateQueries({ queryKey: getAdminMatchDashboardGigsRemindPreviewRetrieveQueryKey(gigId) });
  };
}

function toastApiError(error: unknown, fallback: string) {
  const apiError = ApiError.fromThrown(error);
  toast.error(apiError.getMainErrorMessage() ?? fallback);
}

export function useToggleAutoSelect(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const partialUpdate = useStafferGigsPartialUpdate();
  return useMutation({
    mutationFn: (enabled: boolean) => partialUpdate.mutateAsync({ id: gigId, data: { auto_select_enabled: enabled } }),
    onSuccess: () => invalidate(),
    onError: (error) => toastApiError(error, "Failed to update auto-select"),
  });
}

export function useSetGigBonus(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const partialUpdate = useStafferGigsPartialUpdate();
  return useMutation({
    mutationFn: (amount: number) => partialUpdate.mutateAsync({ id: gigId, data: { boosted_gig_bonus: amount } }),
    onSuccess: () => invalidate(),
  });
}

export function useSetOpenToAll(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const partialUpdate = useStafferGigsPartialUpdate();
  return useMutation({
    // Null clears it, returning the shift to the default rule.
    mutationFn: (minutes: number | null) =>
      partialUpdate.mutateAsync({ id: gigId, data: { open_to_all_minutes_before_start: minutes } }),
    onSuccess: () => invalidate(),
  });
}

/** The three conversion overrides, taken off the generated write model so the field names stay honest. */
export type ConversionFlagUpdate = Pick<
  PatchedStafferGigUpdateRequest,
  "allow_conversion_certs" | "allow_conversion_background_check" | "allow_conversion_w2"
>;

export function useSetConversionFlags(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const partialUpdate = useStafferGigsPartialUpdate();
  return useMutation({
    // Null clears the override back to unset; the dispatcher keeps those pros out either way.
    mutationFn: (data: ConversionFlagUpdate) => partialUpdate.mutateAsync({ id: gigId, data }),
    onSuccess: () => invalidate(),
    onError: (error) => toastApiError(error, "Failed to update the conversion offer"),
  });
}

/** The NARROWING controls (CORE-7740, CORE-8591), separate from the widening trio above. */
export type NarrowCohortUpdate = Pick<
  PatchedStafferGigUpdateRequest,
  "require_skill_experience" | "require_skill_certification" | "auto_open_skilled"
>;

export function useSetNarrowCohortFlags(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const partialUpdate = useStafferGigsPartialUpdate();
  return useMutation({
    // Null clears the override back to unset; unset leaves the cohort alone, same as false.
    mutationFn: (data: NarrowCohortUpdate) => partialUpdate.mutateAsync({ id: gigId, data }),
    onSuccess: () => invalidate(),
    onError: (error) => toastApiError(error, "Failed to update the cohort requirement"),
  });
}

/** The per-shift opt-in that widens a boost announcement past the pros holding an offer. */
export type BoostPushUpdate = Pick<PatchedStafferGigUpdateRequest, "send_boost_push_to_all_eligible">;

export function useSetBoostPushFlag(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const partialUpdate = useStafferGigsPartialUpdate();
  return useMutation({
    // Null clears the override back to unset; unset notifies offered pros only, same as false.
    mutationFn: (data: BoostPushUpdate) => partialUpdate.mutateAsync({ id: gigId, data }),
    onSuccess: () => invalidate(),
    onError: (error) => toastApiError(error, "Failed to update the boost notification setting"),
  });
}

/** The shift-level boost toggles: main switch plus the four gated types (network has no column). Admin-only. */
export type GigBoostSettingsUpdate = Pick<
  PatchedStafferGigUpdateRequest,
  | "boosting_enabled"
  | "boost_manual_enabled"
  | "boost_first_n_gigs_enabled"
  | "boost_late_cancellation_enabled"
  | "boost_offer_enabled"
>;

export function useSetGigBoostSettings(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const partialUpdate = useStafferGigsPartialUpdate();
  return useMutation({
    // Each flip sends only its own field; effective boosting is the server-side AND with the location.
    mutationFn: (data: Partial<GigBoostSettingsUpdate>) => partialUpdate.mutateAsync({ id: gigId, data }),
    onSuccess: () => invalidate(),
    onError: (error) => toastApiError(error, "Failed to update the boost setting"),
  });
}

/**
 * The four ops copy slots (CORE-7926), taken off the generated write model like its siblings.
 */
export type LastCallCopyUpdate = Pick<
  PatchedStafferGigUpdateRequest,
  "last_call_push_title" | "last_call_push_body" | "last_call_conversion_push_title" | "last_call_conversion_push_body"
>;

export function useSetLastCallCopy(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const partialUpdate = useStafferGigsPartialUpdate();
  return useMutation({
    // Blank clears a slot back to "use the preset for this band"; the API treats '' as the unset sentinel.
    mutationFn: (data: LastCallCopyUpdate) => partialUpdate.mutateAsync({ id: gigId, data }),
    onSuccess: () => invalidate(),
    onError: (error) => toastApiError(error, "Failed to update the push copy"),
  });
}

/**
 * What a Last Call blast for this shift would do right now (CORE-7925).
 *
 * Reads SAVED config, so it answers a flipped toggle only once that row's own mutation has settled —
 * which is why `useInvalidateGig` refetches it rather than each toggle wiring its own.
 */
export function useLastCallCohortPreview(gigId: number, enabled: boolean) {
  return useAdminMatchDashboardGigsLastCallPreviewRetrieve(gigId, { query: { enabled } });
}

/** The remind dialog's prefill, who it can reach beyond offer holders, and the cooldown on that (CORE-8433). */
export function useRemindPreview(gigId: number) {
  // Always fresh on open: the prefill carries the pay rate, and the dialog reads it only once.
  return useAdminMatchDashboardGigsRemindPreviewRetrieve(gigId, { query: { refetchOnMount: "always" } });
}

export function useRemindCandidate(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const remind = useStafferGigApplicationsRemindCreate();
  return useMutation({
    mutationFn: (applicationId: number) => remind.mutateAsync({ id: applicationId }),
    onSuccess: () => {
      toast.success("Reminder sent");
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to send reminder"),
  });
}

export function useDismissCandidate(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const destroy = useStafferGigApplicationsDestroy();
  return useMutation({
    mutationFn: (applicationId: number) => destroy.mutateAsync({ id: applicationId }),
    onSuccess: () => {
      toast.success("Candidate dismissed");
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to dismiss candidate"),
  });
}

/**
 * Advisory errors are grouped per reason, not per pro, so show their messages rather than their count. For an async
 * direct offer they are informational and do not shrink `queued_count`, so a partial result does not claim a skip.
 */
export function toastBulkResult(
  response: { queued_count: number; advisory_errors: ReadonlyArray<{ message?: string | null }> },
  successVerb: string,
) {
  const succeeded = response.queued_count;
  const reasons = response.advisory_errors.map((error) => error.message).filter(Boolean);
  if (response.advisory_errors.length === 0) {
    toast.success(`${succeeded} ${successVerb}`);
    return;
  }
  const description = reasons.length > 0 ? reasons.join(" ") : "Some candidates were skipped.";
  const options = { description, duration: 10000 };
  if (succeeded === 0) {
    toast.error(`0 ${successVerb}`, options);
  } else {
    toast.warning(`${succeeded} ${successVerb}`, options);
  }
}

type ReminderCopy = { title: string; body: string };

export function useBulkRemindCandidates(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const bulkRemind = useStafferGigApplicationsBulkRemindCreate();
  return useMutation({
    // Without copy the server sends its standard reminder, which is what the plain "Remind" action wants.
    mutationFn: ({ applicationIds, ...copy }: { applicationIds: ReadonlyArray<number> } & Partial<ReminderCopy>) =>
      bulkRemind.mutateAsync({
        data: {
          application_ids: [...applicationIds],
          custom_title: copy.title ?? null,
          custom_message: copy.body ?? null,
        },
      }),
    onSuccess: (response) => {
      toastBulkResult(response, "reminded");
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to send reminders"),
  });
}

export function useRemindAllCandidates(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const bulkRemind = useStafferGigApplicationsBulkRemindCreate();
  return useMutation({
    mutationFn: ({
      title,
      body,
      includeEligible,
      includeConversion,
    }: ReminderCopy & { includeEligible: boolean; includeConversion: boolean }) =>
      bulkRemind.mutateAsync({
        data: {
          gig_id: gigId,
          application_ids: [],
          custom_title: title,
          custom_message: body,
          include_last_call_eligible: includeEligible,
          include_last_call_conversion: includeConversion,
        },
      }),
    onSuccess: (response) => {
      const reminded = { ...response, queued_count: response.queued_count + response.invited_queued_count };
      if (response.last_call_reminder_queued && response.advisory_errors.length === 0) {
        toast.success(`${reminded.queued_count} reminded`, {
          description: "Pros without an offer get it over the next few minutes.",
        });
      } else {
        toastBulkResult(reminded, "reminded");
      }
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to send reminders"),
  });
}

export function useBulkDismissCandidates(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const bulkDismiss = useStafferGigApplicationsBulkDismissCreate();
  return useMutation({
    mutationFn: (applicationIds: ReadonlyArray<number>) =>
      bulkDismiss.mutateAsync({ data: { application_ids: [...applicationIds] } }),
    onSuccess: (response) => {
      toastBulkResult(response, "dismissed");
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to dismiss candidates"),
  });
}

interface BulkSendDirectOfferArgs {
  proIds: ReadonlyArray<number>;
  overtime?: OvertimeSelection;
  note: Partial<DirectOfferNote>;
  surface: GigOutreachSurfaceEnum;
}

// V1 /api/staffer/gig-applications/ endpoint only takes group_ids/profile_ids — pro_ids is silently dropped, must use V2 direct-offers
export function useBulkSendDirectOffer(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const sendOffer = useStafferDirectOffersCreate();
  return useMutation({
    mutationFn: ({ proIds, overtime, note, surface }: BulkSendDirectOfferArgs) =>
      sendOffer.mutateAsync({ data: { gig_id: gigId, pro_ids: [...proIds], ...overtime, ...note, surface } }),
    onSuccess: (response) => {
      if (Array.isArray(response)) {
        toast.success(`${response.length} sent`);
      } else if ("queued_count" in response) {
        toastBulkResult(response, "queued");
      } else if ("error" in response) {
        toast.error(response.error.message);
      }
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to send direct offers"),
  });
}

export function useUpdateApplicationBonus(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  return useMutation({
    mutationFn: ({ applicationId, boostedGigBonus }: { applicationId: number; boostedGigBonus: number }) =>
      stafferGigApplicationsBonusPartialUpdate(applicationId, { boosted_gig_bonus: boostedGigBonus }),
    onSuccess: () => {
      toast.success("Bonus updated");
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to update bonus"),
  });
}

export function useBulkUpdateBonus(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  return useMutation({
    mutationFn: ({
      applicationIds,
      boostedGigBonus,
    }: {
      applicationIds: ReadonlyArray<number>;
      boostedGigBonus: number;
    }) =>
      stafferGigApplicationsBulkBonusCreate({
        application_ids: [...applicationIds],
        boosted_gig_bonus: boostedGigBonus,
      }),
    onSuccess: (response) => {
      toastBulkResult(response, "bonused");
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to update bonuses"),
  });
}

export function useRemindGrantedPros(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const remind = useStafferGigsGrantedProsRemindCreate();
  return useMutation({
    mutationFn: ({ proIds, ...copy }: { proIds: ReadonlyArray<number> } & Partial<ReminderCopy>) =>
      remind.mutateAsync({
        gigId,
        data: { pro_ids: [...proIds], custom_title: copy.title ?? null, custom_message: copy.body ?? null },
      }),
    onSuccess: (response) => {
      toast.success(`${response.queued_count} reminded`);
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to send reminders"),
  });
}

export function useDismissGrantedPros(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const dismiss = useStafferGigsGrantedProsDismissCreate();
  return useMutation({
    mutationFn: (proIds: ReadonlyArray<number>) => dismiss.mutateAsync({ gigId, data: { pro_ids: [...proIds] } }),
    onSuccess: (response) => {
      toast.success(`${response.revoked_count} dismissed`);
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to dismiss"),
  });
}

export function useUpdateGrantedProsBonus(gigId: number) {
  const invalidate = useInvalidateGig(gigId);
  const bonus = useStafferGigsGrantedProsBonusCreate();
  return useMutation({
    mutationFn: ({ proIds, boostedGigBonus }: { proIds: ReadonlyArray<number>; boostedGigBonus: number }) =>
      bonus.mutateAsync({ gigId, data: { pro_ids: [...proIds], boosted_gig_bonus: boostedGigBonus } }),
    onSuccess: (response) => {
      toast.success(`${response.updated_count} bonused`);
      invalidate();
    },
    onError: (error) => toastApiError(error, "Failed to update bonuses"),
  });
}
